/**
 * CreateTemplateDialog
 *
 * Three-step modal for turning a template generated from a monitoring run
 * (`generateTemplateFromRun`) into a shareable AutoTemplate manifest.json:
 *
 *   1. Metadata — name, description, categories (1–3), featured connectors
 *   2. Review & sanitize — full template JSON in an editor so the user can
 *      remove anything sensitive the auto-scrubber missed
 *   3. Download — a warning stage (Auto keeps no copy) with the download
 *      button, then a next-steps stage with test/share instructions
 *
 * Follows the gallery's view-swap pattern: one Dialog, internal views with
 * back/next navigation and state preserved between steps.
 *
 * The dialog is an alert modal: clicking outside never dismisses it. Before
 * the template has been downloaded, X / Escape ask for confirmation (the
 * generated template only exists in this dialog); after download they close
 * directly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Dropdown,
  Option,
  Input,
  Textarea,
  Field,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@coreai/fluentui-react';
import {
  ArrowDownload16Regular,
  Dismiss24Regular,
  ErrorCircle16Regular,
  ShieldCheckmark16Regular,
} from '@fluentui/react-icons';
import { validateAutoTemplate, type AutoTemplate } from '@/types/autoTemplate';
import {
  TEMPLATE_CATEGORIES,
  CATEGORY_DISPLAY,
  DEFAULT_AUTHORED_TEMPLATE_CATEGORIES,
  MAX_TEMPLATE_CATEGORIES,
  OTHER_TEMPLATE_CATEGORY,
  isTemplateCategory,
  validateAuthoredTemplateCategories,
  type TemplateCategory,
} from '@/types/template';
import { useNotificationStore } from '@/stores/notificationStore';
import { telemetryService } from '@/services/telemetryService';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor/JsonEditor';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { useFeaturedConnector } from '@/hooks/useFeaturedConnector';

// Public GitHub repo template contributions are PR'd to. New templates are
// added as templates/<id>/manifest.json and listed in templates/manifest.json.
const PUBLIC_TEMPLATES_REPO_URL = 'https://github.com/Azure/ProjectAutoTemplates';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  surface: {
    width: '760px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: 0,
    gap: 0,
    rowGap: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    flexShrink: 0,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  stepLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    overflowY: 'auto',
    ...thinScrollbarStyles,
  },
  hint: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  infoTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  infoText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Fixed-height editor that scrolls internally, so the surrounding step
  // body (info box + editor + validation errors) can scroll as a whole when
  // it exceeds the dialog height.
  editorWrapper: {
    height: '45vh',
    minHeight: '240px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    backgroundColor: tokens.colorPaletteRedBackground1,
    maxHeight: '100px',
    overflowY: 'auto',
    flexShrink: 0,
    ...thinScrollbarStyles,
  },
  errorTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  errorItem: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    fontFamily: tokens.fontFamilyMonospace,
  },
  connectorLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  templateNameRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  fileName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  nextStepsList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexShrink: 0,
  },
});

// =============================================================================
// HELPERS
// =============================================================================

type Step = 'metadata' | 'review' | 'download' | 'nextSteps';

const STEP_INFO: Record<Step, { index: number; title: string }> = {
  metadata: { index: 1, title: 'Describe your template' },
  review: { index: 2, title: 'Review & remove sensitive data' },
  download: { index: 3, title: 'Download template' },
  nextSteps: { index: 3, title: 'Next steps' },
};

/** Convert a template name to a URL-safe kebab-case id (same rule as templateFromRun). */
function toKebabCase(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

const INVALID_FILE_NAME_CHARACTERS = /[<>:"/\\|?*]/g;
const RESERVED_WINDOWS_FILE_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;

function toSafeFileNameFragment(id: string): string {
  const withoutControlCharacters = Array.from(id, (character) =>
    character.charCodeAt(0) < 32 ? '-' : character,
  ).join('');
  const sanitized = withoutControlCharacters
    .trim()
    .replace(INVALID_FILE_NAME_CHARACTERS, '-')
    .replace(/-+/g, '-')
    .replace(/^[.\s-]+|[.\s-]+$/g, '');

  if (!sanitized) return 'template';
  return RESERVED_WINDOWS_FILE_NAME.test(sanitized) ? `template-${sanitized}` : sanitized;
}

/**
 * Candidate connector ids for the featured-connector dropdown: the ids the
 * generator already seeded into `metadata.featuredConnectors` (which includes
 * the Request built-in for HTTP-triggered workflows) plus every connector
 * referenced by the template's connections.
 */
function candidateFeaturedIds(template: AutoTemplate): string[] {
  return [
    ...new Set([
      ...template.metadata.featuredConnectors,
      ...Object.values(template.connections).map((c) => c.apiId),
    ]),
  ];
}

interface MetadataFields {
  name: string;
  description: string;
  categories: TemplateCategory[];
}

type TextMetadataField = Exclude<keyof MetadataFields, 'categories'>;

/** Category selection used when the template doesn't carry a valid authored one. */
const DEFAULT_AUTHORED_CATEGORIES: TemplateCategory[] = [...DEFAULT_AUTHORED_TEMPLATE_CATEGORIES];

/**
 * Build the form fields from a template. Categories are adopted only when they
 * satisfy the strict authoring rules; otherwise `fallbackCategories` is kept
 * (never a silently-repaired version of the template's categories).
 */
function metadataFieldsFromTemplate(
  template: AutoTemplate,
  fallbackCategories: TemplateCategory[],
): MetadataFields {
  const categoryResult = validateAuthoredTemplateCategories(template.metadata.category);
  return {
    name: template.metadata.name,
    description: template.metadata.description,
    categories: categoryResult.success ? categoryResult.data : fallbackCategories,
  };
}

/**
 * Selection rules for the category dropdown: toggling a selected category
 * removes it; choosing Other replaces the whole selection; choosing a named
 * category replaces Other; at most MAX_TEMPLATE_CATEGORIES named categories.
 */
function toggleCategory(
  current: TemplateCategory[],
  category: TemplateCategory,
): TemplateCategory[] {
  if (current.includes(category)) return current.filter((c) => c !== category);
  if (category === OTHER_TEMPLATE_CATEGORY) return [OTHER_TEMPLATE_CATEGORY];
  const named = current.filter((c) => c !== OTHER_TEMPLATE_CATEGORY);
  if (named.length >= MAX_TEMPLATE_CATEGORIES) return current;
  return [...named, category];
}

/** `metadata.category` of an arbitrary parsed JSON value, if present. */
function metadataCategoryOf(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return undefined;
  const metadata = (value as { metadata?: unknown }).metadata;
  if (typeof metadata !== 'object' || metadata === null) return undefined;
  return (metadata as { category?: unknown }).category;
}

function isCategoryError(error: string): boolean {
  return error.startsWith('metadata.category') || error.startsWith('metadata:');
}

/**
 * Merge the step-1 fields back into the template's metadata block, including
 * the user's featured-connector selection (id strings — the schema requires
 * at least one; progression is blocked when none is selected). Tags and
 * author are not edited in the dialog: the generator seeds `tags: []` and
 * the signed-in user's name as author, and both pass through untouched.
 */
function applyMetadata(
  template: AutoTemplate,
  fields: MetadataFields,
  featuredConnectors: string[],
): AutoTemplate {
  const name = fields.name.trim();
  return {
    ...template,
    metadata: {
      ...template.metadata,
      id: toKebabCase(name),
      name,
      description: fields.description.trim(),
      category: fields.categories,
      featuredConnectors,
    },
  };
}

// =============================================================================
// FEATURED-CONNECTOR RESOLUTION
// =============================================================================

interface ConnectorDisplayInfo {
  displayName: string;
  iconUri: string | undefined;
}

/**
 * Invisible per-connector resolver. The dropdown's collapsed value needs
 * display names even while the option list is unmounted (Fluent renders
 * options only while the popup is open), and `useFeaturedConnector` is a
 * single-id hook — so one always-mounted resolver per candidate id reports
 * the resolved name/icon up to the dialog.
 */
const FeaturedConnectorInfoResolver: React.FC<{
  apiId: string;
  onResolved: (apiId: string, info: ConnectorDisplayInfo) => void;
}> = ({ apiId, onResolved }) => {
  const { displayName, iconUri } = useFeaturedConnector(apiId);

  useEffect(() => {
    onResolved(apiId, { displayName, iconUri });
  }, [apiId, displayName, iconUri, onResolved]);

  return null;
};

/** Fallback while a resolver hasn't reported yet: last path segment of the id. */
function fallbackConnectorName(apiId: string): string {
  return apiId.split('/').filter(Boolean).pop() ?? apiId;
}

// =============================================================================
// PROPS
// =============================================================================

export interface CreateTemplateDialogProps {
  open: boolean;
  /** The template generated from the run (already auto-sanitized + validated). */
  template: AutoTemplate | null;
  onDismiss: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  open,
  template,
  onDismiss,
}) => {
  const styles = useStyles();

  const [step, setStep] = useState<Step>('metadata');
  const [fields, setFields] = useState<MetadataFields>({
    name: '',
    description: '',
    categories: DEFAULT_AUTHORED_CATEGORIES,
  });
  // Strict category errors from JSON edits that couldn't be carried back into
  // the form; shown on the Category field until the user changes the selection.
  const [categoryWarnings, setCategoryWarnings] = useState<string[]>([]);
  // The template the metadata step builds on. Starts as the generated template
  // and absorbs the user's JSON edits when they navigate back from the review
  // step, so back/forward round-trips don't discard edits.
  const [workingTemplate, setWorkingTemplate] = useState<AutoTemplate | null>(null);
  // Featured-connector dropdown selection (apiId strings). Seeded from the
  // generator's default pick (up to three, serviceProviders > managedApis >
  // connectionProviders).
  const [selectedFeaturedIds, setSelectedFeaturedIds] = useState<string[]>([]);
  // apiId → resolved display name/icon, filled by the invisible resolvers.
  const [connectorInfo, setConnectorInfo] = useState<Record<string, ConnectorDisplayInfo>>({});
  const [jsonText, setJsonText] = useState('');
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);
  const [finalTemplate, setFinalTemplate] = useState<AutoTemplate | null>(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const restoreCloseButtonFocusRef = useRef(false);

  // (Re)initialize when a freshly generated template arrives.
  useEffect(() => {
    if (open && template) {
      setStep('metadata');
      setFields(metadataFieldsFromTemplate(template, DEFAULT_AUTHORED_CATEGORIES));
      setCategoryWarnings([]);
      setWorkingTemplate(template);
      setSelectedFeaturedIds(template.metadata.featuredConnectors);
      setJsonText('');
      setJsonErrors([]);
      setFinalTemplate(null);
      setHasDownloaded(false);
      setShowExitConfirm(false);
      telemetryService.trackEvent('template.generate_opened', {
        templateId: template.metadata.id,
      });
    }
  }, [open, template]);

  const connectorApiIds = useMemo(
    () => (workingTemplate ? candidateFeaturedIds(workingTemplate) : []),
    [workingTemplate],
  );

  const handleConnectorInfoResolved = useCallback(
    (apiId: string, info: ConnectorDisplayInfo) => {
      setConnectorInfo((prev) => {
        const existing = prev[apiId];
        if (existing?.displayName === info.displayName && existing?.iconUri === info.iconUri) {
          return prev;
        }
        return { ...prev, [apiId]: info };
      });
    },
    [],
  );

  const connectorName = useCallback(
    (apiId: string) => connectorInfo[apiId]?.displayName ?? fallbackConnectorName(apiId),
    [connectorInfo],
  );

  const setField = useCallback((key: TextMetadataField, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleCategorySelect = useCallback((optionValue: string | undefined) => {
    if (!isTemplateCategory(optionValue)) return;
    setFields((prev) => ({ ...prev, categories: toggleCategory(prev.categories, optionValue) }));
    setCategoryWarnings([]);
  }, []);

  const isCategorySelectionValid = validateAuthoredTemplateCategories(fields.categories).success;
  const namedCategoryCount = fields.categories.filter(
    (c) => c !== OTHER_TEMPLATE_CATEGORY,
  ).length;
  const isAtCategoryLimit = namedCategoryCount >= MAX_TEMPLATE_CATEGORIES;

  const isMetadataValid =
    fields.name.trim().length > 0 &&
    fields.description.trim().length > 0 &&
    isCategorySelectionValid &&
    // The AutoTemplate schema requires at least one featured connector.
    selectedFeaturedIds.length > 0;

  // Step 1 → 2: fold the metadata fields and the featured-connector selection
  // into the template and serialize it for review. The JSON becomes the single
  // source of truth from here on.
  const handleMetadataNext = useCallback(() => {
    if (!workingTemplate || !isMetadataValid) return;
    setJsonText(
      JSON.stringify(applyMetadata(workingTemplate, fields, selectedFeaturedIds), null, 2),
    );
    setJsonErrors([]);
    setStep('review');
  }, [workingTemplate, fields, isMetadataValid, selectedFeaturedIds]);

  // Step 2 → 1: carry any JSON edits back into the form when they parse —
  // metadata fields, the working template itself, and the dropdown selection
  // (the connectors still present in the JSON's featuredConnectors). Categories
  // are adopted only when they meet the strict authoring rules; otherwise the
  // previous selection is kept and the reason is surfaced on the field.
  const handleReviewBack = useCallback(() => {
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(jsonText) as unknown;
    } catch {
      // Unparseable JSON: keep the form as it was (existing behavior).
    }
    const result = validateAutoTemplate(parsed);
    if (result.success) {
      const categoryResult = validateAuthoredTemplateCategories(result.data.metadata.category);
      setWorkingTemplate(result.data);
      setFields((prev) => metadataFieldsFromTemplate(result.data, prev.categories));
      setCategoryWarnings(categoryResult.success ? [] : categoryResult.errors);
      setSelectedFeaturedIds(result.data.metadata.featuredConnectors);
    } else {
      setCategoryWarnings(result.errors.filter((error) => error.startsWith('metadata.category')));
    }
    setStep('metadata');
  }, [jsonText]);

  const handleJsonChange = useCallback((value: string) => {
    setJsonText(value);
    setJsonErrors([]);
  }, []);

  // Step 2 → 3: gate on JSON parse + full template schema validation + the
  // strict authoring category rules (the general schema stays permissive so
  // imported/stored templates remain backward compatible).
  const handleReviewNext = useCallback(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setJsonErrors([`Not valid JSON: ${err instanceof Error ? err.message : String(err)}`]);
      return;
    }
    const result = validateAutoTemplate(parsed);
    const errors = result.success ? [] : [...result.errors];
    const categoryResult = validateAuthoredTemplateCategories(metadataCategoryOf(parsed));
    if (!categoryResult.success && !errors.some(isCategoryError)) {
      errors.push(...categoryResult.errors);
    }
    if (!result.success || errors.length > 0) {
      setJsonErrors(errors);
      return;
    }
    setFinalTemplate(result.data);
    setStep('download');
  }, [jsonText]);

  // Download name is <id>.manifest.json so files stay distinguishable on disk;
  // in the templates repo/storage the file lives as templates/<id>/manifest.json.
  const fileName = finalTemplate
    ? `${toSafeFileNameFragment(finalTemplate.metadata.id)}.manifest.json`
    : 'manifest.json';

  const handleDownload = useCallback(() => {
    if (!finalTemplate) return;
    const blob = new Blob([JSON.stringify(finalTemplate, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setHasDownloaded(true);
    setStep('nextSteps');
    telemetryService.trackEvent('template.generate_downloaded', {
      templateId: finalTemplate.metadata.id,
    });
    useNotificationStore.getState().addNotification({
      type: 'success',
      title: 'Template downloaded',
      message: `"${finalTemplate.metadata.name}" has been saved as ${fileName}.`,
    });
  }, [finalTemplate, fileName]);

  // X / Escape: close directly once the template has been downloaded;
  // otherwise ask for confirmation — the generated template only exists in
  // this dialog. (Outside clicks never dismiss: modalType="alert".)
  const requestClose = useCallback(() => {
    if (hasDownloaded) {
      onDismiss();
    } else {
      setShowExitConfirm(true);
    }
  }, [hasDownloaded, onDismiss]);

  const handleOpenChange = useCallback(
    (_: unknown, data: { open: boolean }) => {
      if (!data.open) requestClose();
    },
    [requestClose]
  );

  const handleConfirmExit = useCallback(() => {
    setShowExitConfirm(false);
    onDismiss();
  }, [onDismiss]);

  const handleKeepEditing = useCallback(() => {
    restoreCloseButtonFocusRef.current = true;
    setShowExitConfirm(false);
  }, []);

  useEffect(() => {
    if (!showExitConfirm && restoreCloseButtonFocusRef.current) {
      restoreCloseButtonFocusRef.current = false;
      closeButtonRef.current?.focus();
    }
  }, [showExitConfirm]);

  if (!template) return null;

  const stepInfo = STEP_INFO[step];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modalType="alert">
      <DialogSurface
        className={showExitConfirm ? undefined : styles.surface}
        aria-label={showExitConfirm ? 'Exit without downloading' : 'Create template from run'}
      >
        {showExitConfirm ? (
          <DialogBody>
            <DialogTitle>Exit without downloading?</DialogTitle>
            <DialogContent>
              Your template hasn&apos;t been downloaded. Auto won&apos;t save a copy — if
              you exit now, you&apos;ll need to recreate it.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleKeepEditing}>
                Keep editing
              </Button>
              <Button appearance="primary" onClick={handleConfirmExit}>
                Exit
              </Button>
            </DialogActions>
          </DialogBody>
        ) : (
          <DialogBody className={styles.body}>
            {/* Header */}
            <div className={styles.header}>
              <Text as="h2" className={styles.headerTitle}>
                {stepInfo.title}
              </Text>
              <Text className={styles.stepLabel}>Step {stepInfo.index} of 3</Text>
              <Button
                ref={(element: HTMLButtonElement | null) => {
                  closeButtonRef.current = element;
                }}
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={requestClose}
                aria-label="Close create template dialog"
              />
            </div>

            {/* Step 1 — Metadata */}
            {step === 'metadata' && (
              <>
                {connectorApiIds.map((apiId) => (
                  <FeaturedConnectorInfoResolver
                    key={apiId}
                    apiId={apiId}
                    onResolved={handleConnectorInfoResolved}
                  />
                ))}
                <div className={styles.content}>
                  <Text className={styles.hint}>
                    This template was generated from the selected run. Give it a name and
                    description so others can find and understand it.
                  </Text>
                  <Field label="Name" required>
                    <Input
                      value={fields.name}
                      onChange={(_, data) => setField('name', data.value)}
                      placeholder="e.g. Send weekly status email"
                      aria-label="Template name"
                    />
                  </Field>
                  <Field label="Description" required>
                    <Textarea
                      value={fields.description}
                      onChange={(_, data) => setField('description', data.value)}
                      placeholder="What does this workflow do, and when would someone use it?"
                      resize="vertical"
                      aria-label="Template description"
                    />
                  </Field>
                  <Field
                    label="Category"
                    required
                    hint={
                      isAtCategoryLimit
                        ? `Maximum of ${MAX_TEMPLATE_CATEGORIES} categories selected. Deselect one to choose another.`
                        : `Choose up to ${MAX_TEMPLATE_CATEGORIES} categories. Other can't be combined with other categories.`
                    }
                    {...(fields.categories.length === 0
                      ? { validationState: 'error' as const, validationMessage: 'Select at least one category' }
                      : categoryWarnings.length > 0
                        ? {
                            validationState: 'warning' as const,
                            validationMessage: `The JSON's categories weren't applied: ${categoryWarnings.join('; ')}`,
                          }
                        : {})}
                  >
                    <Dropdown
                      multiselect
                      placeholder="Select categories"
                      aria-label="Template category"
                      value={fields.categories.map((c) => CATEGORY_DISPLAY[c]).join(', ')}
                      selectedOptions={fields.categories}
                      onOptionSelect={(_, data) => handleCategorySelect(data.optionValue)}
                    >
                      {TEMPLATE_CATEGORIES.map((cat) => (
                        <Option
                          key={cat}
                          value={cat}
                          text={CATEGORY_DISPLAY[cat]}
                          disabled={
                            isAtCategoryLimit &&
                            cat !== OTHER_TEMPLATE_CATEGORY &&
                            !fields.categories.includes(cat)
                          }
                        >
                          {CATEGORY_DISPLAY[cat]}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                  {connectorApiIds.length > 0 && (
                    <Field
                      label="Featured connectors"
                      hint="Shown as icons on the template card — at least one is required"
                      {...(selectedFeaturedIds.length === 0
                        ? { validationState: 'error' as const, validationMessage: 'Select at least one featured connector' }
                        : {})}
                    >
                      <Dropdown
                        multiselect
                        placeholder="Select featured connectors"
                        aria-label="Featured connectors"
                        value={selectedFeaturedIds.map(connectorName).join(', ')}
                        selectedOptions={selectedFeaturedIds}
                        onOptionSelect={(_, data) => setSelectedFeaturedIds(data.selectedOptions)}
                      >
                        {connectorApiIds.map((apiId) => (
                          <Option key={apiId} value={apiId} text={connectorName(apiId)}>
                            <span className={styles.connectorLabel}>
                              <ConnectorIcon
                                {...(connectorInfo[apiId]?.iconUri
                                  ? { iconUri: connectorInfo[apiId]?.iconUri }
                                  : {})}
                                size={16}
                              />
                              {connectorName(apiId)}
                            </span>
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  )}
                </div>
                <div className={styles.footer}>
                  <Button appearance="secondary" onClick={requestClose}>
                    Cancel
                  </Button>
                  <Button appearance="primary" onClick={handleMetadataNext} disabled={!isMetadataValid}>
                    Next: Review JSON
                  </Button>
                </div>
              </>
            )}

            {/* Step 2 — Review & sanitize */}
            {step === 'review' && (
              <>
                <div className={styles.content}>
                  <div className={styles.infoBox}>
                    <Text className={styles.infoTitle}>
                      <ShieldCheckmark16Regular aria-hidden="true" />
                      Automatic sanitization already applied
                    </Text>
                    <Text className={styles.infoText}>
                      Credential headers (authorization, cookies, API keys) and SAS query
                      parameters were redacted from the trigger sample, infrastructure
                      headers were removed, and response headers were stripped from action
                      mocks. Still, review the JSON below for anything the scrubber can't
                      know about — names, email addresses, internal URLs, or sample data
                      you don't want to share — and edit it out before continuing.
                    </Text>
                  </div>
                  <div className={styles.editorWrapper}>
                    <JsonEditor
                      value={jsonText}
                      onChange={handleJsonChange}
                      fill
                      hasError={jsonErrors.length > 0}
                    />
                  </div>
                  {jsonErrors.length > 0 && (
                    <div className={styles.errorBox} role="alert">
                      <Text className={styles.errorTitle}>
                        <ErrorCircle16Regular aria-hidden="true" />
                        The edited JSON is not a valid template
                      </Text>
                      {jsonErrors.map((err, i) => (
                        <Text key={i} className={styles.errorItem}>
                          {err}
                        </Text>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.footer}>
                  <Button appearance="secondary" onClick={handleReviewBack}>
                    Back
                  </Button>
                  <Button appearance="primary" onClick={handleReviewNext}>
                    Next: Download
                  </Button>
                </div>
              </>
            )}

            {/* Step 3a — Download */}
            {step === 'download' && finalTemplate && (
              <>
                <div className={styles.content}>
                  <Text className={styles.hint}>
                    Download the template before you close this dialog. Auto won&apos;t save
                    a copy — if you leave without downloading it, you&apos;ll need to
                    recreate it.
                  </Text>
                  <Text className={styles.templateNameRow}>
                    <Text weight="semibold">Template name:</Text>
                    <span className={styles.fileName}>{fileName}</span>
                  </Text>
                </div>
                <div className={styles.footer}>
                  <Button appearance="secondary" onClick={() => setStep('review')}>
                    Back
                  </Button>
                  <Button
                    appearance="primary"
                    icon={<ArrowDownload16Regular />}
                    onClick={handleDownload}
                  >
                    Download template
                  </Button>
                </div>
              </>
            )}

            {/* Step 3b — Next steps (after download) */}
            {step === 'nextSteps' && finalTemplate && (
              <>
                <div className={styles.content}>
                  <Text className={styles.hint}>
                    Your template download is ready. You can now test the template in Auto
                    or share it with others.
                  </Text>
                  <ul className={styles.nextStepsList}>
                    <li>
                      <strong>Test the template:</strong> import the JSON from the template
                      gallery&apos;s <strong>Import from JSON</strong> card.
                    </li>
                    <li>
                      <strong>Share the template:</strong>{' '}
                      <a href={PUBLIC_TEMPLATES_REPO_URL} target="_blank" rel="noreferrer">
                        view the template contribution guide
                      </a>{' '}
                      to publish it for everyone via pull request.
                    </li>
                  </ul>
                </div>
                <div className={styles.footer}>
                  <Button appearance="primary" onClick={onDismiss}>
                    Done
                  </Button>
                </div>
              </>
            )}
          </DialogBody>
        )}
      </DialogSurface>
    </Dialog>
  );
};

CreateTemplateDialog.displayName = 'CreateTemplateDialog';
